import { useState } from "react";

import { Column, Row, Text } from "@/components";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Button } from "@ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";

import { useUpdateSetShiftPreferences } from "@/hooks/updateSets/useUpdateSetShiftPreferences";
import { shiftTypeToLabel } from "@/lib/constants";
import { ShiftPreferenceStatus, ShiftType } from "@/types";

const SHIFT_ORDER = [
  ShiftType.FIRSTSHIFT,
  ShiftType.SECONDSHIFT,
  ShiftType.THIRDSHIFT,
  ShiftType.WEEKEND,
];
// Strongest stance first, matching how the Shift row reads its value out.
const STATUS_ORDER = [
  ShiftPreferenceStatus.PREFERRED,
  ShiftPreferenceStatus.YES,
  ShiftPreferenceStatus.NO,
];
const statusToLabel: Record<ShiftPreferenceStatus, string> = {
  [ShiftPreferenceStatus.NO]: "No",
  [ShiftPreferenceStatus.PREFERRED]: "Preferred",
  [ShiftPreferenceStatus.YES]: "Yes",
};
// Shifts the candidate never spoke about have no row. They start blank and stay
// that way unless the staffer picks something.
const UNKNOWN_PLACEHOLDER = "—";

export const ShiftPreferenceEditor = ({
  onDone,
  shiftPreferences,
  stafferId,
  userId,
}: {
  onDone: () => void;
  shiftPreferences: { shift: string; status: string }[];
  stafferId: number;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Update
  const { isPending, mutate: saveShiftPreferences } =
    useUpdateSetShiftPreferences();

  /* React Hooks */
  const [drafts, setDrafts] = useState<
    Partial<Record<ShiftType, ShiftPreferenceStatus>>
  >(() =>
    Object.fromEntries(
      shiftPreferences.map(({ shift, status }) => [shift, status]),
    ),
  );

  /* Helpers */
  const savedByShift = Object.fromEntries(
    shiftPreferences.map(({ shift, status }) => [shift, status]),
  ) as Partial<Record<ShiftType, ShiftPreferenceStatus>>;

  /* Handlers */
  const handleSelect = (shift: ShiftType, status: ShiftPreferenceStatus) => {
    setDrafts((currentDrafts) => ({ ...currentDrafts, [shift]: status }));
  };

  const handleDone = () => {
    const entries = SHIFT_ORDER.flatMap((shift) => {
      const draft = drafts[shift];
      // Untouched shifts, and shifts left blank, are omitted rather than sent.
      if (!draft || draft === savedByShift[shift]) return [];
      return [{ shift, status: draft }];
    });

    if (!entries.length) {
      onDone();
      return;
    }

    saveShiftPreferences({ entries, stafferId, userId }, { onSuccess: onDone });
  };

  /* Render */
  const renderShiftSelect = (shift: ShiftType) => {
    return (
      <Column className="gap-1" key={shift}>
        <Text className="font-plex-mono text-[9px] font-medium uppercase tracking-wider text-slate-400">
          {shiftTypeToLabel[shift]}
        </Text>
        <Select
          disabled={isPending}
          onValueChange={(status) => {
            handleSelect(shift, status as ShiftPreferenceStatus);
          }}
          value={drafts[shift]}
        >
          <SelectTrigger className="h-7 w-28 text-sm">
            <SelectValue placeholder={UNKNOWN_PLACEHOLDER} />
          </SelectTrigger>
          <SelectContent>
            {STATUS_ORDER.map((status) => (
              <SelectItem key={status} value={status}>
                {statusToLabel[status]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Column>
    );
  };

  const render = () => {
    return (
      <Row className="flex-wrap items-end gap-3">
        {SHIFT_ORDER.map((shift) => renderShiftSelect(shift))}
        <Button
          className="h-7 px-3 text-xs"
          disabled={isPending}
          onClick={handleDone}
          size="sm"
          type="button"
        >
          {isPending ? (
            <Row className="items-center gap-1.5">
              <LoadingIndicator gearColor="white" variant="small" />
              Saving
            </Row>
          ) : (
            "Done"
          )}
        </Button>
      </Row>
    );
  };

  return render();
};
